import { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { User, onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth'
import { initializeFirebase, getFirebaseAuth } from '@/lib/firebase'
import { api, ApiError } from '@/api/baseApi'
import { fetchMe } from '@/api/riskPortal.api'
import type { RiskPortalProfile } from '@/types/riskPortal.types'

interface AuthContextType {
  /** The Firebase user, or null when signed out. */
  user: User | null
  /** True while Firebase is initializing / resolving the initial auth state. */
  loading: boolean
  /** The risk-assessment profile once resolved; null before resolution or when not authorized. */
  riskUser: RiskPortalProfile | null
  /**
   * null = still resolving; true = signed in AND a valid active risk-assessment user;
   * false = signed in to Firebase but not a risk-assessment user (or the check failed).
   */
  isAuthorizedRiskUser: boolean | null
  signInWithEmail: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [riskUser, setRiskUser] = useState<RiskPortalProfile | null>(null)
  const [isAuthorizedRiskUser, setIsAuthorizedRiskUser] = useState<boolean | null>(null)
  // Tracks the last authenticated Firebase uid so we can wipe the React Query cache whenever the
  // identity changes (logout or switching accounts). The query cache is a module-level singleton, so
  // without this the next user would briefly see the previous user's cached documents/banking.
  const previousUidRef = useRef<string | null>(null)

  useEffect(() => {
    let unsubscribe: (() => void) | undefined

    const init = async () => {
      try {
        await initializeFirebase()
        const auth = getFirebaseAuth()

        api.setTokenGetter(async (forceRefresh: boolean) => {
          const currentUser = auth.currentUser
          if (!currentUser) return null
          return currentUser.getIdToken(forceRefresh)
        })

        unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
          setUser(firebaseUser)
          setLoading(false)

          if (!firebaseUser) {
            setRiskUser(null)
            setIsAuthorizedRiskUser(null)
          }
        })
      } catch (error) {
        console.error('Firebase initialization failed:', error)
        setLoading(false)
      }
    }

    init()

    return () => {
      unsubscribe?.()
    }
  }, [])

  // Whenever the authenticated identity changes (signed out, or switched to a different account),
  // drop every cached query so no stale data leaks from the previous session into the next one.
  useEffect(() => {
    const currentUid = user?.uid ?? null
    if (previousUidRef.current !== null && previousUidRef.current !== currentUid) {
      queryClient.clear()
    }
    previousUidRef.current = currentUid
  }, [user?.uid, queryClient])

  // Once a Firebase user is present, confirm they are an active risk-assessment user and load their
  // profile (region drives the required-documents flow). A 401/403 means "authenticated but not a
  // risk-assessment user" → not authorized (handled by ProtectedRoute).
  useEffect(() => {
    if (!user) return

    let cancelled = false

    const resolveRiskUser = async () => {
      try {
        const profile = await fetchMe()
        if (cancelled) return
        setRiskUser(profile)
        setIsAuthorizedRiskUser(true)
      } catch (error) {
        if (cancelled) return
        setRiskUser(null)
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          setIsAuthorizedRiskUser(false)
        } else {
          console.error('Failed to resolve risk-assessment profile:', error)
          setIsAuthorizedRiskUser(false)
        }
      }
    }

    resolveRiskUser()

    return () => {
      cancelled = true
    }
  }, [user?.uid])

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const auth = getFirebaseAuth()
    await signInWithEmailAndPassword(auth, email.trim(), password)
  }, [])

  const logout = useCallback(async () => {
    const auth = getFirebaseAuth()
    await signOut(auth)
    setUser(null)
    setRiskUser(null)
    setIsAuthorizedRiskUser(null)
    // Wipe the shared query cache immediately so a subsequent login can't render this user's data.
    queryClient.clear()
  }, [queryClient])

  return (
    <AuthContext.Provider
      value={{ user, loading, riskUser, isAuthorizedRiskUser, signInWithEmail, logout }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
